import { NumericFormat, NumberFormatValues } from 'react-number-format';
import { useOptionalSettings } from 'context/SettingsContext';
import {
  CSSProperties,
  FocusEvent,
  MouseEvent,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  getBaseCurrencyPrefix,
  getIsLocalCurrencyMode,
  getLocalCurrencyPrefix,
} from 'utils/helpers/currency/currencyHelpers';
import SvgEdit3 from 'components/svg/SvgEdit3';

interface Props {
  value: number;
  onValueChange?: (values: any) => void;
  required?: boolean;
  allowNegative?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  id?: string;
  /** Курс, якщо компонент рендериться поза SettingsProvider (діалог ModalProvider). */
  rate?: number;
} 

const selectAll = (input: HTMLInputElement | null) => {
  if (!input) return;
  // react-number-format після focus ставить каретку в setTimeout(0)
  // і затирає виділення. Наступний тік виділяє вже після цього.
  window.setTimeout(() => {
    if (document.activeElement !== input) return;
    input.select();
  }, 0);
};

const keepSelection = (e: MouseEvent<HTMLInputElement>) => {
  e.preventDefault();
};

const InputPriceRate = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onValueChange,
      required = false,
      allowNegative = true,
      disabled = false,
      style,
      id,
      rate: rateProp,
    },
    ref
  ) => {
    const settings = useOptionalSettings();
    const rate = Number(rateProp ?? settings?.localSettings?.RATE?.current ?? 1) || 1;
    const isLocalMode = getIsLocalCurrencyMode(rate);
    const localPrefix = getLocalCurrencyPrefix();
    const basePrefix = getBaseCurrencyPrefix();
    const baseInputRef = useRef<HTMLInputElement | null>(null);
    const localInputRef = useRef<HTMLInputElement | null>(null);

    const [valueLocal, setValueLocal] = useState(value * rate);
    const [editLocal, setEditLocal] = useState(false);

    useEffect(() => {
      if (isLocalMode) {
        setEditLocal(false);
        return;
      }
      if (!editLocal) {
        setValueLocal(value * rate);
      }
    }, [value, rate, isLocalMode, editLocal]);

    const focusAndSelect = (input: HTMLInputElement | null) => {
      if (!input) return;
      input.focus();
      selectAll(input);
    };

    const startEditLocal = () => {
      setEditLocal(true);
      requestAnimationFrame(() => focusAndSelect(localInputRef.current));
    };

    const startEditBase = () => {
      setEditLocal(false);
      requestAnimationFrame(() => focusAndSelect(baseInputRef.current));
    };

    const emitBase = (base: number | undefined, values?: NumberFormatValues) => {
      onValueChange?.({
        ...values,
        floatValue: base,
        value: base == null ? '' : String(base),
      });
    };

    const handleLocalValueChange = (values: NumberFormatValues) => {
      const local = values.floatValue;
      setValueLocal(local ?? 0);
      if (local == null) {
        emitBase(undefined, values);
        return;
      }
      emitBase(Number((local / rate).toFixed(2)), values);
    };

    const toggleBtn = (onClick: () => void) => (
      <button
        type="button"
        className="input-price-rate__toggle"
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        tabIndex={-1}
      >
        <SvgEdit3 width={14} height={14} style={{ display: 'block' }} />
      </button>
    );

    return (
      <div className="d-flex input-price-rate" style={style}>
        <div className={`input-price-rate__field${editLocal && !disabled ? ' has-toggle' : ''}`}>
          <NumericFormat
            id={id}
            disabled={disabled}
            readOnly={editLocal}
            tabIndex={editLocal ? -1 : undefined}
            allowNegative={allowNegative}
            className={`form-control object-price${editLocal ? ' disable' : ''}`}
            style={{
              borderRadius: isLocalMode
                ? 'var(--bs-border-radius)'
                : 'var(--bs-border-radius) 0px 0px var(--bs-border-radius)',
            }}
            prefix={isLocalMode ? localPrefix : basePrefix}
            thousandSeparator=" "
            decimalSeparator="."
            allowedDecimalSeparators={['.', ',']}
            decimalScale={2}
            allowLeadingZeros
            value={value}
            required={required}
            onValueChange={editLocal ? undefined : onValueChange}
            onMouseUp={keepSelection}
            onFocus={(e: FocusEvent<HTMLInputElement>) => {
              if (editLocal) {
                startEditBase();
                return;
              }
              selectAll(e.currentTarget);
            }}
            getInputRef={(el: HTMLInputElement) => {
              baseInputRef.current = el;
              if (!ref) return;
              if (typeof ref === 'function') {
                ref(el);
              } else {
                ref.current = el;
              }
            }}
          />
          {editLocal && !disabled && toggleBtn(startEditBase)}
        </div>

        {!isLocalMode && (
          <div className={`input-price-rate__field${ !editLocal && !disabled ? ' has-toggle' : ''}`}>
            <NumericFormat
              className={`form-control object-price${editLocal ? '' : ' disable'}`}
              style={{
                borderRadius: '0px var(--bs-border-radius) var(--bs-border-radius) 0px',
                borderLeft: 'none',
              }}
              prefix={localPrefix}
              disabled={disabled}
              readOnly={!editLocal}
              tabIndex={editLocal ? undefined : -1}
              allowNegative={allowNegative}
              value={valueLocal}
              thousandSeparator=" "
              decimalSeparator="."
              allowedDecimalSeparators={['.', ',']}
              decimalScale={2}
              allowLeadingZeros
              onValueChange={editLocal ? handleLocalValueChange : undefined}
              onMouseUp={keepSelection}
              onFocus={(e: FocusEvent<HTMLInputElement>) => {
                if (!editLocal) {
                  startEditLocal();
                  return;
                }
                selectAll(e.currentTarget);
              }}
              getInputRef={(el: HTMLInputElement) => {
                localInputRef.current = el;
              }}
            />
            {!editLocal && !disabled && toggleBtn(startEditLocal)}
          </div>
        )}
      </div>
    );
  }
);

export default InputPriceRate;
